'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React from 'react'
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  RouteObject
} from 'react-router-dom'
import moment from 'moment'

// lib
import AppProviders from '@components/app-providers'
import { RouteError } from '@components/error-boundary'
import { useAuthStore, selectIsAuthenticated } from '@store/Auth'
import Session from '@components/session'
import Chat from '@screens/chat/chat.web'
import Dashboard from '@screens/dashboard/dashboard.web'
import MedicationHistory from '@screens/medication-history/medication-history.web'
import GoalActions from '@screens/goal-actions/goal-actions.web'
import Home from '@screens/home/home.web'
import QueuePrioritization from '@screens/queue-prioritization/queue-prioritization.web'
import Opportunities from '@screens/opportunities/opportunities.web'
import Category from '@screens/opportunities/custom/category/category.web'
import OpportunityDetail from '@screens/opportunities/custom/opportunity-detail/opportunity-detail.web'
import Login from '@screens/login'
import PasswordReset, { PasswordResetSuccess } from '@screens/password-reset'
import Members from '@screens/members/members.web'
import CompanyMembers from '@screens/members/company-members/company-members.web'
import Settings from '@screens/settings/settings.web'
import AuthDevices from '@screens/auth-devices/auth-devices.web'

/* -----------------------------------------------------------------------------
 * createRouter
 * -------------------------------------------------------------------------- */

export type RouterOptions = {
  isAuthenticated?: boolean
}

// every route gets the same error screen: without one react-router renders a
// raw stack trace in place of the whole app
const withErrorElement = (routes: RouteObject[]): RouteObject[] =>
  routes.map(route => ({ errorElement: <RouteError />, ...route }))

const createRouter = ({ isAuthenticated }: RouterOptions) =>
  createBrowserRouter(
    withErrorElement([
      {
        path: '/',
        element: isAuthenticated ? (
          <Navigate to='/home' replace />
        ) : (
          <Navigate to='/login' replace />
        )
      },
      {
        path: '/login',
        element: isAuthenticated ? <Navigate to='/home' replace /> : <Login />
      },
      {
        path: '/settings',
        element: isAuthenticated ? (
          <Settings />
        ) : (
          <Navigate to='/login' replace />
        )
      },
      ...(isAuthenticated
        ? [
            {
              path: 'dashboard',
              element: <Dashboard />
            },
            {
              path: 'home',
              element: <Home />
            },
            {
              path: 'queue',
              element: <QueuePrioritization />
            },
            {
              path: 'opportunities',
              element: <Opportunities />,
              children: [
                {
                  path: 'unique',
                  element: <Opportunities />,
                  children: [
                    {
                      path: ':ruleName',
                      element: <Opportunities />
                    }
                  ]
                }
              ]
            },
            {
              path: 'opportunities/category/:categoryId',
              element: <Category />
            },
            {
              path: 'opportunities/category/:categoryId/opportunity/:opportunityId',
              element: <OpportunityDetail />
            },
            {
              path: 'chat',
              element: <Chat />,
              children: [
                {
                  path: ':chatId',
                  element: <Chat />,
                  children: [
                    {
                      path: ':roomId',
                      element: <Chat />
                    }
                  ]
                }
              ]
            },
            {
              path: 'medication-history',
              element: <MedicationHistory />,
              children: [
                {
                  path: ':userId',
                  element: <MedicationHistory />
                }
              ]
            },
            {
              path: 'goal-actions',
              element: <GoalActions />,
              children: [
                {
                  path: ':userId',
                  element: <GoalActions />
                }
              ]
            },
            {
              path: 'members',
              element: <Members />
            },
            {
              path: 'members/company/:companyId',
              element: <CompanyMembers />
            },
            {
              path: '/settings/devices',
              element: <AuthDevices />
            },
            {
              path: '/settings/password-reset',
              element: <PasswordReset />
            },
            {
              path: '/settings/password-reset/success',
              element: <PasswordResetSuccess />
            }
          ]
        : []),
      {
        path: '*',
        element: isAuthenticated ? (
          <Navigate to='/home' replace />
        ) : (
          <Navigate to='/login' replace />
        )
      }
    ])
  )

/* -----------------------------------------------------------------------------
 * Router
 * -------------------------------------------------------------------------- */

const Router = () => {
  const isAuthenticated = useAuthStore(selectIsAuthenticated)
  const router = createRouter({ isAuthenticated })

  return <RouterProvider router={router} />
}

/* -----------------------------------------------------------------------------
 * ProfessionalApp
 * -------------------------------------------------------------------------- */

function ProfessionalApp() {
  moment.locale('en')

  return (
    <AppProviders>
      <Session>
        <Router />
      </Session>
    </AppProviders>
  )
}

export default ProfessionalApp
